<script setup lang="ts">
import { FieldArray, type PrivateFormContext, type useForm } from 'vee-validate'
import CrdFloatingInput from '@/components/common/CrdFloatingInput.vue'
import { useTranslations } from '@/composables/useTranslations.ts'
import { nextTick, ref, computed, inject, watch, onMounted, onUnmounted } from 'vue'
import ColorSelector from '@/components/projects/settings/visual/ColorSelector.vue'
import MarginSelector from '@/components/projects/settings/visual/MarginSelector.vue'
import { type IWidgetAssistantPhoto, type IWidgetLocalization, } from '@/components/projects/interfaces.ts'
import GradientSelector from '@/components/projects/settings/visual/GradientSelector.vue'
import { cloneDeep, isEqual, isFunction, isNull } from 'lodash'
import {
  WIDGET_ASSISTANT_IFRAME_DESKTOP,
  WIDGET_ASSISTANT_IFRAME_MOBILE,
} from '@/config/visual.const.ts'
import type { FormSettings } from '@/components/interfaces/project-settings-view.interfaces.ts'
import AvatarSelector from '@/components/projects/settings/visual/AvatarSelector.vue'
import { PostMessageType, ProjectSettingsCategory, ProjectSettingsField, ProvideElement } from '@/enums.ts'
import { useUserStore } from '@/stores/user.store.ts'
import { storeToRefs } from 'pinia'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import CrdRange from '@/components/common/CrdRange.vue'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { API_WIDGET_URL } from '@/config/env.const.ts'
import CrdBlock from '@/components//common/CrdBlock.vue'
import CrdButton from '@/components/common/crd-button/CrdButton.vue'
import { ButtonType, ButtonVariant } from '@/components/common/crd-button/crd-button.enums.ts'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/20/solid'
import AssistantLanguageSelector from '@/components/projects/settings/assistant/AssistantLanguageSelector.vue'
import type { PlatformLanguage } from '@/components/interfaces/graphql-common.interfaces.ts'
import { ProjectSettingFormKey, ProjectSettingSyncInitialStateKey } from '@/components/projects/settings.types.ts'
import ProjectSettingsForm from '@/components/projects/ProjectSettingsForm.vue'
import { useProjectSettingStore } from '@/stores/project-settings.store.ts'

const props = defineProps<{
  projectId: number
  platformLanguages: PlatformLanguage[]
  widgetAssistantPhotos: IWidgetAssistantPhoto[]
}>()
const {
  widgetAssistantBehaviour: widgetAssistantBehaviourSettings,
  common
} = inject(ProvideElement.ProjectSettingsFormSettings) as FormSettings
const form = inject(ProjectSettingFormKey)
const [ widgetLocalization ] = form.defineField(ProjectSettingsCategory.WidgetLocalization)
const [ widgetVisual ] = form.defineField(ProjectSettingsCategory.WidgetVisual)
const widgetLocalizationLocale = ref<string>(widgetLocalization.value.find((l: IWidgetLocalization) => l.default)?.locale ?? 'en')// TODO: Const
const emit = defineEmits<{
  (e: 'reset'): void
}>()
const { t } = useTranslations()
const isIframeLoaded = ref(false)
const userStore = useUserStore()
const projectSettingsStore = useProjectSettingStore()
const { projectSelected } = storeToRefs(userStore)
const { mobileIframeRef, desktopIframeRef, integrationIframeRef, isWidgetRedrawRequired } = storeToRefs(projectSettingsStore)
const widgetRedrawInterval = null
const isOpen = ref(false)
let scrollPosition = 0
// TODO: Enum with locale
const isInitialMessageRangeSliderDisabled = computed<boolean>(() => {
  return widgetLocalization.value.every(({ initialMessage }: IWidgetLocalization) => initialMessage.trim().length === 0)
})

watch(widgetLocalizationLocale, (newLocale) => {
  updateLocalization()
}, { immediate: true })

function openModal(): void {
  scrollPosition = window.scrollY
  isOpen.value = true
  window.scrollTo(0, scrollPosition)
}

function closeModal(): void {
  isOpen.value = false

  nextTick(() => {
    window.scrollTo(0, scrollPosition)
  })
}

function updateVisualSettings(): void {
  postMessageToChatWidgetIframes({
    type: PostMessageType.CROWDY_UPDATE_VISUAL_SETTINGS,
    settings: cloneDeep(widgetVisual.value),
  })
}

function updateLocalization(): void {
  postMessageToChatWidgetIframes({
    type: PostMessageType.CROWDY_UPDATE_LOCALIZATION,
    locale: widgetLocalizationLocale.value,
    localization: cloneDeep(widgetLocalization.value) ?? null,
  })
}

function postMessageToChatWidgetIframes (payload: { type: PostMessageType } & Record<string, any>): void {
  if (!isIframeLoaded.value) {
    return
  }

  [mobileIframeRef.value, desktopIframeRef.value, integrationIframeRef.value]
  .filter((el) => el instanceof HTMLIFrameElement)
  .forEach((iframe) => {
    if (isFunction(iframe?.contentWindow?.postMessage)) {
      try {
        iframe.contentWindow.postMessage(payload, '*')
      } catch (err) {
        console.error('AssistantTab | postMessage failed:', err)
        console.warn('AssistantTab | postMessage failed | paylaod', payload)
      }
    }
  })
}

function onIframeLoaded() {
  isIframeLoaded.value = true

  updateVisualSettings()
  updateLocalization()
}

function onDisableGradients() {
  widgetVisual.value.gradients.enabled = false
}

onMounted(() =>{
})

onUnmounted(() => {
  if (!isNull(widgetRedrawInterval)) {
    clearInterval(widgetRedrawInterval)
  }
})

watch(() => widgetVisual, () => {
  updateVisualSettings()
}, { deep: true })
watch(() => widgetLocalization, (newLang, oldLang) => {
  updateLocalization()
}, { immediate: false, deep: true })
watch(() => isWidgetRedrawRequired, (newFlag) => {
  if (newFlag) {
    updateVisualSettings()
    updateLocalization()
  }
}, { immediate: false })
</script>

<template>
  <div class="flex flex-row justify-between">
    <div class="flex flex-col md:w-1/2">
      <CrdBlock
        class="bg-white flex-col mb-4 p-4 rounded-md shadow-xs"
        :class-custom="{ 'gap-0': true }"
      >
        <p class="crd-header-text">{{ t('settings.primary.localization') }}</p>
        <AssistantLanguageSelector :languages="platformLanguages" v-model="widgetLocalizationLocale"/>
        <FieldArray name="widgetLocalization" v-slot="{ fields, push, remove }" >
          <fieldset
            v-for="(field, index) in fields"
            :key="(field.value as IWidgetLocalization).locale"
            :class="{ hidden: widgetLocalization[index].locale != widgetLocalizationLocale }"
          >
            <CrdFloatingInput :label="t('settings.primary.assistantName')" :name="`widgetLocalization[${index}].assistantName`" />
            <CrdFloatingInput :label="t('settings.primary.assistantBadge')" :name="`widgetLocalization[${index}].assistantBadge`"/>
            <CrdFloatingInput :label="t('settings.primary.initialMessage')" :name="`widgetLocalization[${index}].initialMessage`"/>
            <CrdFloatingInput :label="t('settings.primary.placeholder')" :name="`widgetLocalization[${index}].userMessagePlaceholder`"/>
          </fieldset>
        </FieldArray>
      </CrdBlock>
      <div class="*:mb-2 *:p-4 *:flex-col *:text-center">
        <CrdBlock>
          <p class="text-sm md:text-base font-semibold">{{ t('settings.primary.chooseAvatar') }}</p>
          <AvatarSelector :project-id="projectId" :photos="widgetAssistantPhotos"/>
        </CrdBlock>

        <CrdBlock>
          <p class="text-sm md:text-base font-semibold">{{ t('settings.primary.chooseWidgetColor') }}</p>
          <ColorSelector
            v-model="widgetVisual.color"
            name="widgetVisual.color"
            @color-selected="onDisableGradients"
            :isColorEnable="!widgetVisual.gradients.enabled"
          />
        </CrdBlock>
        <CrdBlock>
          <GradientSelector/>
        </CrdBlock>
        <CrdBlock>
          <p class="text-sm md:text-base font-semibold">{{ t('settings.primary.chooseDistanceFromCorner') }}</p>
          <MarginSelector/>
        </CrdBlock>
      </div>
      <div class="flex flex-col *:my-2">
        <CrdBlock
          :class="{
            'section-wrapper': true,
            'section-wrapper--disabled': !isIframeLoaded,
          }"
        >
          <CrdRange
            unit="ms"
            :name="ProjectSettingsField.InitialMessageTypingDelayMs"
            :min="widgetAssistantBehaviourSettings.initialMessageTypingDelayMs.min"
            :max="widgetAssistantBehaviourSettings.initialMessageTypingDelayMs.max"
            :step="widgetAssistantBehaviourSettings.initialMessageTypingDelayMs.step"
            :label="t('settings.primary.initialMessageTypingTime')"
            :description="t('settings.primary.initialMessageTypingTimeDescription')"
            :disabled="isInitialMessageRangeSliderDisabled"
          />
        </CrdBlock>
        <CrdBlock
          :class="{
            'section-wrapper': true,
            'section-wrapper--disabled': !isIframeLoaded,
          }"
        >
          <CrdRange
            unit="ms"
            :name="ProjectSettingsField.InitialMessageSendingDelayAfterTypingEndedMs"
            :min="widgetAssistantBehaviourSettings.initialMessageSendingDelayAfterTypingEndedMs.min"
            :max="widgetAssistantBehaviourSettings.initialMessageSendingDelayAfterTypingEndedMs.max"
            :step="widgetAssistantBehaviourSettings.initialMessageSendingDelayAfterTypingEndedMs.step"
            :label="t('settings.primary.initialMessageSendingDelayAfterTypingEndedMs')"
            :description="t('settings.primary.initialMessageSendingDelayAfterTypingEndedMsDescription')"
            :disabled="isInitialMessageRangeSliderDisabled"
          />
        </CrdBlock>
        <CrdBlock
          :class="{
            'section-wrapper': true,
            'section-wrapper--disabled': !isIframeLoaded,
          }"
        >
          <CrdRange
            unit="ms"
            :name="ProjectSettingsField.DelayBeforeResponseMs"
            :min="widgetAssistantBehaviourSettings.delayBeforeResponseMs.min"
            :max="widgetAssistantBehaviourSettings.delayBeforeResponseMs.max"
            :step="widgetAssistantBehaviourSettings.delayBeforeResponseMs.step"
            :label="t('settings.primary.delayBeforeResponse')"
            :description="t('settings.primary.delayBeforeResponseDescription')"
          />
        </CrdBlock>
        <CrdBlock
          :class="{
            'section-wrapper': true,
            'section-wrapper--disabled': !isIframeLoaded,
          }"
        >
          <CrdRange
            unit="min"
            :name="ProjectSettingsField.ChatAliveForXMinutes"
            :min="common.chatAliveForXMinutes.min"
            :max="common.chatAliveForXMinutes.max"
            :step="common.chatAliveForXMinutes.step"
            :label="t('settings.primary.chatAliveForXMinutes')"
            :description="t('settings.primary.chatAliveForXMinutesDescription')"
          />
        </CrdBlock>
      </div>
    </div>
    <div class="max-md:hidden py-4 mx-auto">
      <div class="sticky top-40 right-0">
        <iframe
          name="crowdy-ai-iframe"
          :id="WIDGET_ASSISTANT_IFRAME_DESKTOP"
          @load="onIframeLoaded"
          :src="`${API_WIDGET_URL}?projectKey=${projectSelected?.key ?? ''}&themeSelectionMode=true&referrer=aHR0cHM6Ly9jcm93ZHkuYWk=`"
          :class="{
            'crowdy-ai-iframe rounded-xl h-[calc(60vh)] min-h-56 max-h-[400px] w-[calc(40vw)] min-w-[300px] max-w-[500px] z-10': true,
          }"
          :crowdy-project-key="projectSelected?.key ?? ''"
          auto-open-delay-ms="2500"
          collapsed-if-width-less="640"
          frameborder="0"
          allow="camera; microphone; autoplay;"
          ref="desktopIframeRef"
        >
        </iframe>
        <div class="mt-3 flex items-center justify-center gap-x-3">
          <CrdButton
            class="px-4 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            :variant="ButtonVariant.Save"
            :type="ButtonType.Submit"
          >
            <CheckCircleIcon class="block size-5" aria-hidden="true" />
            <span>{{ t('common.buttons.save') }}</span>
          </CrdButton>
          <CrdButton
            class="px-4 py-2 bg-indigo-200 text-indigo-700 rounded-md hover:bg-gray-300 disabled:opacity-50"
            :variant="ButtonVariant.Cancel"
            @click="emit('reset')"
          >
            <XCircleIcon class="block size-5" aria-hidden="true" />
            {{ t('common.buttons.cancel') }}
          </CrdButton>
        </div>
      </div>
    </div>
    <div
      class="md:hidden overflow-auto fixed bottom-12 right-12 size-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300"
      @click="openModal"
    >
      <img src="/favicon.png" alt="Assistant" class="size-8" />
      <TransitionRoot appear :show="isOpen" as="div">
        <Dialog class="relative z-10" as="div" @close="closeModal" :open="isOpen">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <div class="fixed inset-0 bg-gray-500/80" />
          </TransitionChild>

          <div class="fixed inset-0 overflow-y-auto z-10">
            <div class="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
              >
                <DialogPanel
                  class="w-full max-w-md transform overflow-hidden rounded-xl bg-white p-4 text-left align-middle shadow-xl transition-all max-h-[80vh] flex flex-col"
                >
                  <div class="flex-1 overflow-y-auto">
                    <iframe
                      :id="WIDGET_ASSISTANT_IFRAME_MOBILE"
                      @load="onIframeLoaded"
                      :src="`${API_WIDGET_URL}?projectKey=${projectSelected?.key ?? ''}&themeSelectionMode=true&referrer=aHR0cHM6Ly9jcm93ZHkuYWk=`"
                      class="crowdy-ai-iframe rounded-xl z-10"
                      :crowdy-project-key="projectSelected?.key ?? ''"
                      auto-open-delay-ms="2500"
                      collapsed-if-width-less="640"
                      frameborder="0"
                      height="100%"
                      width="100%"
                      allow="camera; microphone; autoplay;"
                      ref="mobileIframeRef"
                    >
                    </iframe>
                  </div>
                  <div class="mt-4 flex justify-center">
                    <button
                      class="py-2 px-4 bg-purple-custom rounded-xl text-xl text-white"
                      @click="closeModal"
                    >
                      <font-awesome-icon :icon="faArrowLeft" class="text-white mr-2" />
                      {{ t('settings.primary.backToEdit') }}
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </TransitionRoot>
    </div>
  </div>
</template>

<style scoped>
@reference "../../../../index.css";

.visual-tab iframe {
  @apply rounded-[10px] overflow-hidden;
}
.section-wrapper {
  @apply p-4 space-y-4 bg-white rounded-lg shadow-sm;
}
.section-wrapper--disabled {
  @apply opacity-50 pointer-events-none;
}
</style>
